import './ApplicationGroupCompositionGraph.scss';

import { ApplicationGroup } from '@amzn/awscat-portfolio-assessment-types';
import {
    Box,
    Button,
    Checkbox,
    Container,
    FormField,
    Grid,
    GridProps,
    Input,
    Modal,
    RadioGroup,
    RadioGroupProps,
    SpaceBetween,
    Spinner,
    Toggle,
} from '@amzn/awsui-components-react';
import { Data, Layout } from 'plotly.js';
import PlotlyBasic from 'plotly.js-basic-dist';
import { createRef, useContext, useEffect, useState } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import { match, useHistory, useRouteMatch } from 'react-router-dom';

import ApplicationGroupingLocalStorage from '../../../common/ApplicationGroupingLocalStorage';
import { applicationGroupCompositionConstants, constants } from '../../../common/Localization';
import { useResize } from '../../../common/resize-hook';
import { MatchParam } from '../../../common/types';
import { deepCopy } from '../../../common/util';
import { CookieConsentContext } from '../../../contexts/CookieConsentContext';
import FeatureNotAvailable from '../../common/FeatureNotAvailable';
import { DEFAULT_WAVE_CAPACITY, MIN_Y_VALUE_WITHOUT_REPEAT_TICK } from './constants';
const PlotlyChart = createPlotlyComponent(PlotlyBasic);

interface ApplicationGroupGraphData {
    tickVals: number[];
    tickText: string[];
    numApp: number[];
    appColor: string[];
    numServer: number[];
    serverColor: string[];
    numDB: number[];
    dbColor: string[];
};

const emptyGraphData: ApplicationGroupGraphData = {
    tickVals: [],
    tickText: [],
    numApp: [],
    appColor: [],
    numServer: [],
    serverColor: [],
    numDB: [],
    dbColor: [],
};

const defaultAppColor = 'rgb(207,107,16)';
const defaultServerColor = 'rgb(255,153,0)';
const defaultDBColor = 'rgb(0,115,187)';
const overLimitColor = 'rgb(209,50,18)';

interface ApplicationGroupCompositionGraphProp {
    applicationGroups: ApplicationGroup[];
    loading: boolean;
    inferGroups: () => void;
}

const viewOptions: RadioGroupProps.RadioButtonDefinition[] = [
    { value: '15', label: applicationGroupCompositionConstants.GROUPS_15 },
    { value: '30', label: applicationGroupCompositionConstants.GROUPS_30 },
    { value: '45', label: applicationGroupCompositionConstants.GROUPS_45 },
    { value: '60', label: applicationGroupCompositionConstants.GROUPS_60 },
];

const sortOptions: RadioGroupProps.RadioButtonDefinition[] = [
    { value: 'ranking', label: applicationGroupCompositionConstants.RANKING },
    { value: 'totalApplications', label: applicationGroupCompositionConstants.NUMBER_OF_APPLICATIONS },
    { value: 'totalServers', label: applicationGroupCompositionConstants.NUMBER_OF_SERVERS },
    { value: 'totalDatabases', label: applicationGroupCompositionConstants.NUMBER_OF_DATABASES },
    { value: 'name', label: applicationGroupCompositionConstants.GROUP_NAME },
];

interface GroupCompositionPreferences {
    viewSize: number;
    assetsToDisplay: {
        applications: boolean;
        servers: boolean;
        databases: boolean;
    };
    sortOption: string;
}

const defaultPreferences: GroupCompositionPreferences = {
    viewSize: 15,
    assetsToDisplay: {
        applications: true,
        servers: false,
        databases: false,
    },
    sortOption: 'totalApplications',
};

/**
 * Compares 2 groups based on the sortkey and sortorder
 * @param {Object} a 
 * @param {Object} b 
 * @returns an integer 1, -1, or 0 representing order of a vs b, depending on the sortkey and sort order
 */
const compareGroups = (sortKey: keyof ApplicationGroup, sortOrder: number) => (a: ApplicationGroup, b: ApplicationGroup)  => {
    const propertyA = a[sortKey];
    const propertyB = b[sortKey];

    let comparision = 0;
    if (propertyA === null) {
        comparision = 1;
    }
    else if (propertyB === null) {
        comparision = -1;
    }
    else {
        comparision = propertyA?.toString().localeCompare(propertyB?.toString(), 'en', { numeric: true });
    }
    return comparision * sortOrder;
};

function ApplicationGroupCompositionGraph({ applicationGroups, loading, inferGroups }: ApplicationGroupCompositionGraphProp) {

    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });

    const portfolioId = match?.params?.portfolioId;

    const history = useHistory();

    const [ graphData, setGraphData ] = useState<ApplicationGroupGraphData>(emptyGraphData);
    const [ preferencesModalVisible, setPreferencesModalVisible ] = useState(false);
    const [ preferences, setPreferences ] = useState<GroupCompositionPreferences>({...defaultPreferences});
    const [ modalPreferences, setModalPreferences ] = useState<GroupCompositionPreferences>({...defaultPreferences});
    const [ groupsSorted, setSortedGroups ] = useState<ApplicationGroup[]>([]);
    const [ showWaveCapacity, setShowWaveCapacity ] = useState(false);
    const [ applicationWaveCapacity, setApplicationWaveCapacity ] = useState(DEFAULT_WAVE_CAPACITY.applications);
    const [ serverWaveCapacity, setServerWaveCapacity ] = useState(DEFAULT_WAVE_CAPACITY.servers);
    const [ databaseWaveCapacity, setDatabaseWaveCapacity ] = useState(DEFAULT_WAVE_CAPACITY.databases);
    const [ hasCookieConsent, setHasCookieConsent ] = useState(false);

    const { cookie, getLocalStorage, setLocalStorage } = useContext(CookieConsentContext);

    const { assetsToDisplay, viewSize, sortOption } = preferences;

    // this is needed since the margin properties in the graph layout are not setting the width correctly
    const componentRef = createRef<HTMLDivElement>();
    const graphDimensions = useResize(componentRef);

    useEffect(() => {
        setHasCookieConsent(cookie.functional);
    }, [ cookie ]);

    const generateGraphData = (groups: ApplicationGroup[]): void => {
        const sortOrder = (sortOption === 'name' || sortOption === 'ranking') ? 1 : -1;
        const sortedGroups = [...groups].sort(compareGroups(sortOption as keyof ApplicationGroup, sortOrder));
        setSortedGroups(sortedGroups);
        // need to deep copy the existing object to trigger a rerender
        const updatedGraphData: ApplicationGroupGraphData = deepCopy(emptyGraphData);
        const groupNameMaxLength = 15;

        const { tickVals, tickText, numApp, appColor, numServer, serverColor, numDB, dbColor } = updatedGraphData;
        sortedGroups.forEach((group: ApplicationGroup, index) => {
            const groupName = (group.name.length > groupNameMaxLength) ? group.name.substring(0, groupNameMaxLength) + '...' : group.name;

            tickVals.push(index);
            tickText.push(groupName);
            numApp.push(group.totalApplications);
            appColor.push(showWaveCapacity && group.applicationIds.length > applicationWaveCapacity ? overLimitColor : defaultAppColor);
            numServer.push(group.totalServers);
            serverColor.push(showWaveCapacity && group.serverIds.length > serverWaveCapacity ? overLimitColor : defaultServerColor);
            numDB.push(group.totalDatabases);
            dbColor.push(showWaveCapacity && group.databaseIds.length > databaseWaveCapacity ? overLimitColor : defaultDBColor);
        });
        setGraphData(updatedGraphData);
    };

    useEffect(() => {
        if (portfolioId) {
            const waveCapacity = ApplicationGroupingLocalStorage.getCapacity(portfolioId, getLocalStorage);
            if (waveCapacity) {
                setApplicationWaveCapacity(waveCapacity.applications);
                setServerWaveCapacity(waveCapacity.servers);
                setDatabaseWaveCapacity(waveCapacity.databases);
            } else {
                ApplicationGroupingLocalStorage.saveWaveCapacity(portfolioId, 
                    {servers: DEFAULT_WAVE_CAPACITY.servers, applications: DEFAULT_WAVE_CAPACITY.applications, databases: DEFAULT_WAVE_CAPACITY.databases},
                    getLocalStorage, setLocalStorage);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        generateGraphData(applicationGroups);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationGroups, preferences]);

    const cfg = {
        displaylogo: false,
        displayModeBar: false,
        resposive: true
    };

    const getLayout = (): Partial<Layout> => {
        const { numDB, numApp, numServer } = graphData;
        const { databases, applications, servers } = assetsToDisplay;
        const maxArr = [...(databases ? numDB : []), ...(applications ? numApp : []), ...(servers ? numServer : [])];
        const maxVal = maxArr.length > 0 ? maxArr.reduce((a, b) => Math.max(a, b)) : 0;
        return {
            dragmode: 'pan',
            height: 550,
            width: graphDimensions.width,
            showlegend: false,
            yaxis: {
                fixedrange: true,
                tickformat: ',d',
                tickmode: maxVal > MIN_Y_VALUE_WITHOUT_REPEAT_TICK ? 'array' : 'linear'
            },
            xaxis: {
                range: [-0.5, viewSize - 0.5],
                tickangle: -45,
                ticktext: graphData.tickText,
                tickvals: graphData.tickVals,
                fixedrange: false
            },
            margin: {
                t: 10,
                b: 100,
                r: 40,
                l: 40
            }
        };};

    const graph_data: Data[] = [
        {
            x: [-.5, groupsSorted.length - .5],
            y: [applicationWaveCapacity, applicationWaveCapacity],
            type: 'scatter',
            visible: showWaveCapacity && assetsToDisplay.applications,
            mode: 'lines',
            line: {
                color: defaultAppColor,
                width: 3
            }
        },
        {
            x: [-.5, groupsSorted.length - .5],
            y: [serverWaveCapacity, serverWaveCapacity],
            type: 'scatter',
            visible: showWaveCapacity && assetsToDisplay.servers,
            mode: 'lines',
            line: {
                color: defaultServerColor,
                width: 3
            }
        },
        {
            x: [-.5, groupsSorted.length - .5],
            y: [databaseWaveCapacity, databaseWaveCapacity],
            type: 'scatter',
            visible: showWaveCapacity && assetsToDisplay.databases,
            mode: 'lines',
            line: {
                color: defaultDBColor,
                width: 3
            }
        },
        {
            y: graphData.numApp,
            type: 'bar',
            hoverinfo: 'none',
            marker: {
                color: graphData.appColor,
            },
            visible: assetsToDisplay.applications,
            showlegend: false
        },
        {
            y: graphData.numServer,
            type: 'bar',
            hoverinfo: 'none',
            marker: {
                color: graphData.serverColor,
            },
            visible: assetsToDisplay.servers,
            showlegend: false
        },
        {
            y: graphData.numDB,
            type: 'bar',
            hoverinfo: 'none',
            marker: {
                color: graphData.dbColor,
            },
            visible: assetsToDisplay.databases,
            showlegend: false
        }
    ];

    const modalGridDefinition: GridProps.ElementDefinition[] = [
        {colspan: 6},
        {colspan: 6},
        {colspan: 6},
        {colspan: 6}
    ];

    const onModalOpen = () => {
        setModalPreferences({...preferences});
        setPreferencesModalVisible(true);
    };

    const onViewOptionChange = (value: string) => {
        setModalPreferences({
            ...modalPreferences,
            viewSize: parseInt(value),
        });
    };

    const onSortOptionChange = (value: string) => {
        setModalPreferences({
            ...modalPreferences,
            sortOption: value,
        });
    };

    const onAssetsToViewChange = (type: 'applications' | 'servers' | 'databases', checked: boolean) => {
        const updatedPreferences = {...modalPreferences};
        updatedPreferences.assetsToDisplay[type] = checked;
        setModalPreferences(updatedPreferences);
    };

    const onModalDismiss = () => {
        setModalPreferences({...preferences});
        setPreferencesModalVisible(false);
    };

    const onModalSave = () => {
        setPreferences({...modalPreferences});
        if (portfolioId) {
            ApplicationGroupingLocalStorage.saveWaveCapacity(portfolioId, 
                {applications: applicationWaveCapacity, servers: serverWaveCapacity, databases: databaseWaveCapacity},
                getLocalStorage, setLocalStorage);
        }
        setPreferencesModalVisible(false);
    };

    const onGraphClick = ({ points }: Readonly<PlotlyBasic.PlotMouseEvent>) => {
        const index = points[0].x;
        const clickedGroup = (typeof index === 'number') ?groupsSorted[index] : null;
        if (clickedGroup) {
            history.push(`/portfolio/${portfolioId}/planning/grouping/${clickedGroup.applicationGroupId}`);
        }
    };

    const isWaveCapacityOutOfBound = (value: number): boolean => {
        return value < 1 || value > 1000;
    };

    const content = applicationGroups.length >= 1 ? (
        <span>
            <Box padding={{left: 'xl'}} display='inline-block'>
                <h2 className='awsui-util-d-ib awsui-util-f-l'>
                    {applicationGroupCompositionConstants.GRAPH_TITLE_NUMBER_OF}
                    {assetsToDisplay.applications ?
                        <span style={{color: defaultAppColor}}> {applicationGroupCompositionConstants.APPLICATIONS}</span>
                        : null
                    }
                    {assetsToDisplay.applications && assetsToDisplay.servers && assetsToDisplay.databases
                        ? ',' : assetsToDisplay.applications && (assetsToDisplay.servers || assetsToDisplay.databases)
                            ? applicationGroupCompositionConstants.AND : ''}
                    {assetsToDisplay.servers ?
                        <span style={{color: defaultServerColor}}> {applicationGroupCompositionConstants.SERVERS}</span>
                        : null
                    }
                    {assetsToDisplay.servers && assetsToDisplay.databases ? applicationGroupCompositionConstants.AND : ''}
                    {assetsToDisplay.databases ?
                        <span style={{color: defaultDBColor}}> {applicationGroupCompositionConstants.DATABASES}</span>
                        : null
                    }
                    &nbsp;{applicationGroupCompositionConstants.GRAPH_TITLE_PER_GROUP}
                </h2>
            </Box>
            <Box display='inline-block' float='right'>
                <Button onClick={onModalOpen} iconName='settings'></Button>
            </Box>
            <Grid gridDefinition={[{colspan: 12}]}>
                <div id='application-group-composition-plotly-graph' ref={componentRef}>
                    <PlotlyChart data={graph_data} layout={getLayout()}  config={cfg} onClick={onGraphClick} />
                </div>
            </Grid>
        </span>
    ) : (
        <Box textAlign='center' id='begin-app-grouping' className='landing-container'>
            <h1>{applicationGroupCompositionConstants.APPLICATION_GROUPING_TITLE}</h1>
            <p>
                {applicationGroupCompositionConstants.NO_GROUPS_MESSAGE_1}
                <b>{constants.GET_STARTED_BUTTON_TEXT}</b>
                {applicationGroupCompositionConstants.NO_GROUPS_MESSAGE_2}
            </p>
            <span>{applicationGroupCompositionConstants.DATA_REQUIRED}</span>
            <ul>
                <li>{applicationGroupCompositionConstants.SERVER}</li>
                <li>{applicationGroupCompositionConstants.APPLICATION}</li>
                <li>{applicationGroupCompositionConstants.SERVER_APP_MAPPING}</li>
            </ul>
            <Box padding={{top: 'xl'}}>
                <Button onClick={inferGroups} variant='primary' id='btn-getstarted-dependency'>
                    {constants.GET_STARTED_BUTTON_TEXT}
                </Button>
            </Box>
        </Box>
    );

    return (
        <div>
            <Container>
                <div id='group-composition-graph'>
                    {!loading ? content : <Spinner size='big' />}
                </div>
            </Container>
            <Modal
                visible={preferencesModalVisible}
                onDismiss={onModalDismiss}
                header={applicationGroupCompositionConstants.PREFERENCES_MODAL_HEADER}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button onClick={onModalDismiss} variant="link">{constants.CANCEL_BUTTON_TEXT}</Button>
                            <Button onClick={onModalSave} variant="primary">{constants.CONFIRM_BUTTON_TEXT}</Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <Grid gridDefinition={modalGridDefinition}>
                    <div>
                        <h3>{applicationGroupCompositionConstants.ASSETS_TO_DISPLAY}</h3>
                        <Checkbox
                            onChange={event => onAssetsToViewChange('applications', event.detail.checked)}
                            checked={modalPreferences.assetsToDisplay.applications}
                        >
                            {constants.APPLICATIONS}
                        </Checkbox>
                        <Checkbox
                            onChange={event => onAssetsToViewChange('servers', event.detail.checked)}
                            checked={modalPreferences.assetsToDisplay.servers}
                        >
                            {constants.SERVERS}
                        </Checkbox>
                        <Checkbox
                            onChange={event => onAssetsToViewChange('databases', event.detail.checked)}
                            checked={modalPreferences.assetsToDisplay.databases}
                        >
                            {constants.DATABASES}
                        </Checkbox>
                    </div>
                    <div>
                        <h3>{applicationGroupCompositionConstants.DEFAULT_VIEW}</h3>
                        <RadioGroup
                            value={modalPreferences.viewSize.toString()}
                            items={viewOptions}
                            onChange={event => onViewOptionChange(event.detail.value)}
                        />
                    </div>
                    <div>
                        <h3>{applicationGroupCompositionConstants.SORT_OPTIONS}</h3>
                        <RadioGroup
                            value={modalPreferences.sortOption}
                            items={sortOptions}
                            onChange={event => onSortOptionChange(event.detail.value)}
                        />
                    </div>
                    <div>
                        <h3>{constants.WAVE_CAPACITY}</h3>
                        { hasCookieConsent ? (
                            <SpaceBetween size='xxs' direction='vertical'>
                                <Toggle 
                                    checked={showWaveCapacity}
                                    onChange={event => setShowWaveCapacity(event.detail.checked)}
                                >
                                    {applicationGroupCompositionConstants.SHOW_WAVE_CAPACITY}
                                </Toggle>
                                <FormField 
                                    label={constants.APPLICATIONS}
                                    errorText={isWaveCapacityOutOfBound(applicationWaveCapacity) ? 
                                        applicationGroupCompositionConstants.OUT_OF_BOUNDS_ERROR : ''}
                                >
                                    <Input 
                                        value={applicationWaveCapacity.toString()} 
                                        type='number' 
                                        onChange={event => setApplicationWaveCapacity(Number(event.detail.value))}
                                    />
                                </FormField>
                                <FormField
                                    label={constants.SERVERS}
                                    errorText={isWaveCapacityOutOfBound(serverWaveCapacity) ? applicationGroupCompositionConstants.OUT_OF_BOUNDS_ERROR : ''}
                                >
                                    <Input 
                                        value={serverWaveCapacity.toString()} 
                                        type='number'
                                        onChange={event => setServerWaveCapacity(Number(event.detail.value))}
                                    />
                                </FormField>
                                <FormField
                                    label={constants.DATABASES}
                                    errorText={isWaveCapacityOutOfBound(databaseWaveCapacity) ? applicationGroupCompositionConstants.OUT_OF_BOUNDS_ERROR : ''}
                                >
                                    <Input 
                                        value={databaseWaveCapacity.toString()} 
                                        type='number'
                                        onChange={event => setDatabaseWaveCapacity(Number(event.detail.value))}
                                    />
                                </FormField>
                            </SpaceBetween>
                        ) : <FeatureNotAvailable /> }
                    </div>
                </Grid>
            </Modal>
        </div>
    );

}

export default ApplicationGroupCompositionGraph;
